/**
 * Core getters and lifecycle for the gallery builder component.
 */
export function createGalleryBuilderCore(initial_layout_count = 2, initial_orientation = 'horizontal', collectionId = null) {
  return {
    collectionId: collectionId,

    search_query: '',
    search_service: null,
    get layout_count() { return this.$store.galleryCore.layout_count; },
    get orientation() { return this.$store.galleryCore.orientation; },
    get selected_artworks() { return this.$store.galleryCore.selected_artworks; },
    get gallery_visible() { return this.$store.galleryCore.gallery_visible; },
    get third_artwork_visible() { return this.$store.galleryCore.third_artwork_visible; },
    get layout_type() { return this.$store.galleryCore.layout_type; },
    get layout_css_class() { return this.$store.galleryCore.layout_css_class; },
    get ui_state() {
      return {
        loading: this.$store.galleryUI.loading,
        error: this.$store.galleryUI.error,
        randomizing: this.$store.galleryUI.randomizing,
        active_artwork_index: this.$store.galleryUI.active_artwork_index,
        hover_artwork_index: this.$store.galleryUI.hover_artwork_index,
        user_has_interacted: this.$store.galleryUI.user_has_interacted,
        retry_count: this.$store.galleryUI.retry_count,
        loading_fadeout: this.$store.galleryUI.loading_fadeout,
        config_modal_open: this.$store.galleryUI.config_modal_open,
        transitioning_layout: this.$store.galleryUI.transitioning_layout
      };
    },
    get all_products() {
      // Return combined products from both orientations for backward compatibility
      const horizontal = this.$store.galleryProducts.horizontal_products || [];
      const vertical = this.$store.galleryProducts.vertical_products || [];
      return [...horizontal, ...vertical];
    },
    get filtered_artworks() { return this.$store.galleryProducts.filtered_artworks; },
    get can_randomize() { return this.$store.galleryProducts.can_randomize; },
    get works_count_display() { return this.$store.galleryProducts.works_count_display; },
    get gallery_complete() { return this.$store.galleryCore.gallery_complete; },
    get set_type_display() { return `${this.layout_type.toUpperCase()} SET`; },
    get set_type_display_mobile() { return this.$store.galleryCore.set_type_display_mobile; },
    get layout_icon_name() { return this.$store.galleryCore.layout_icon_name; },
    get total_price_display() { return this.$store.galleryCore.total_price_display; },
    get required_artwork_count() { return this.$store.galleryCore.required_artwork_count; },
    get loading_progress() { return this.$store.galleryUI.loading_progress; },
    get current_loading_text() { return this.$store.galleryUI.current_loading_text; },
    get text_is_changing() { return this.$store.galleryUI.text_is_changing; },
    get target_progress() { return this.$store.galleryUI.target_progress; },
    get cache_performance() { return this.$store.galleryProducts.cache_performance; },
    get artwork_count() { return this.$store.galleryProducts.artwork_count; },
    get is_loading() { return this.$store.galleryGlobal.loading; },
    get collections_loaded() { return this.$store.galleryGlobal.collections_loaded; },
    get randomizing() { return this.$store.galleryUI.randomizing; },
    get variant_modal_open() { return this.$store.galleryUI.variant_modal_open; },
    get show_configure_button() {
      // Show configure button when no size has been selected AND user has clicked an artwork
      const { size } = this.$store.galleryCore.variant_selection;
      const hasInteracted = this.$store.galleryUI.user_has_interacted;
      return size === null && hasInteracted;
    },
    get dynamic_price_display() {
      // Show "from" price when no size selected, actual total price when size is selected
      // Used by both desktop and mobile price displays
      const { size } = this.$store.galleryCore.variant_selection;
      return size === null ? this.$store.galleryCore.min_price_display : this.total_price_display;
    },

    get filter_modal_open() { return this.$store.galleryUI.filter_modal_open; },
    get has_active_filters() { return this.$store.galleryProducts.has_active_filters; },
    get filter_count_display() { return this.$store.galleryProducts.filter_count_display; },
    get totalActiveFilters() { return this.$store.galleryProducts.boost_filter_count; },
    get show_edit_button() {
      const { size } = this.$store.galleryCore.variant_selection;
      const modalOpen = this.$store.galleryUI.variant_modal_open;
      return size !== null && !modalOpen;
    },
    get show_add_to_cart_button() {
      const { size } = this.$store.galleryCore.variant_selection;
      return size !== null;
    },

    // Mobile-aware button text getters
    get edit_button_text() {
      const translations = window.galleryTranslations?.buttons || {};
      return this.isMobile()
        ? (translations.edit_mobile || 'Size, Frame & Mount')
        : (translations.edit || 'Edit Size, Frame & Mount');
    },
    get add_to_cart_button_text() {
      const translations = window.galleryTranslations?.buttons || {};
      const baseText = this.isMobile()
        ? (translations.add_to_cart_mobile || 'Add to Cart')
        : (translations.add_to_cart || 'Add Set to Cart');
      return `${baseText} - ${this.total_price_display || '$0'}`;
    },

    // Preview mode getters
    get preview_mode() { return this.$store.galleryPreview.preview_mode; },
    get preview_transitioning() { return this.$store.galleryPreview.preview_transitioning; },
    get preview_ready() { return this.$store.galleryPreview.preview_ready; },
    get zoom_active() { return this.$store.galleryPreview.zoom_active; },
    get zoomed_artwork_index() { return this.$store.galleryPreview.zoomed_artwork_index; },
    get preview_button_disabled() { return this.$store.galleryUI.preview_button_disabled; },
    get exit_preview_visible() { return this.$store.galleryUI.exit_preview_visible; },
    get preview_cursor_mode() { return this.$store.galleryUI.preview_cursor_mode; },

    init() {
      if (this.$store.galleryCore.init) {
        this.$store.galleryCore.init();
      }

      this.$store.galleryCore.layout_count = initial_layout_count;
      this.$store.galleryCore.orientation = initial_orientation;
      this.$store.galleryCore.third_artwork_visible = initial_layout_count === 3;

      if (typeof this.initializeSharedStateFromUrl === 'function') {
        this.initializeSharedStateFromUrl();
      }

      this.$el.addEventListener('gallery-retry-loading', () => {
        this.loadCollectionProducts();
      });

      this.$el.addEventListener('gallery-exit', () => {
        this.handleExit();
      });

      this.$el.addEventListener('gallery-add-to-cart', async (event) => {
        await this.handleAddToCart(event.detail);
      });

      this.loadCollectionProducts();

      if (this.isMobile && this.isMobile()) {
        setTimeout(() => {
          if (this.initMobileCarousel) {
            this.initMobileCarousel();
          }
        }, 1500);

        this.setupMobileCarouselWatcher();

        this.$watch('$store.galleryUI.active_search_query', (newQuery, oldQuery) => {
          if (!this.isMobile || !this.isMobile()) return;
          if (newQuery === oldQuery) return;

          // Wait for search completion
          setTimeout(() => {
            if (this.refreshMobileCarousel) {
              this.refreshMobileCarousel();
            }
          }, 500);
        });

        document.addEventListener('gallery:search-completed', (event) => {
          if (!this.isMobile || !this.isMobile()) return;

          // Small delay to ensure stores are updated
          setTimeout(() => {
            if (this.refreshMobileCarousel) {
              this.refreshMobileCarousel();
            }
          }, 100);
        });

        this.$watch('$store.galleryCore.orientation', (newOrientation, oldOrientation) => {
          if (!this.isMobile || !this.isMobile()) return;
          if (!oldOrientation) return;

          if (this.updateMobileCarouselOrientation) {
            this.updateMobileCarouselOrientation();
          }
        });
      }

      this.initializeBoostFilters();
    },

    async initializeBoostFilters() {
      const isFilterEnabled = this.$el.dataset.filtersEnabled === 'true' ||
                             window.location.pathname.includes('gallery-builder');

      if (!isFilterEnabled) {
        return;
      }

      try {
        // Dynamically import the bridge to keep bundle size down
        const { BoostGalleryBridge } = await import('../services/boost/boost-gallery-bridge.js');

        const bridge = new BoostGalleryBridge(this.collectionId);
        await bridge.initialize({
          products: this.$store.galleryProducts,
          ui: this.$store.galleryUI,
          core: this.$store.galleryCore
        });

        await this.initializeSearch();
      } catch (error) {
        console.warn('⚠️ Failed to initialize Boost filters:', error);
      }
    },

    // Search functionality methods
    async initializeSearch() {
      if (!window.galleryBoostBridge) {
        return;
      }

      const { GallerySearchService } = await import('../services/search/gallery-search-service.js');

      const stores = {
        products: this.$store.galleryProducts,
        ui: this.$store.galleryUI,
        core: this.$store.galleryCore
      };

      if (!window.gallerySearchService) {
        window.gallerySearchService = new GallerySearchService(
          window.galleryBoostBridge,
          stores
        );
      } else {
        window.gallerySearchService.updateStores(stores);
      }

      this.search_service = window.gallerySearchService;
    },

    // Handle search input
    handleSearchInput(event) {
      const query = event.target.value;
      this.search_query = query;

      if (this.search_service) {
        this.search_service.handleSearchInput(query);
      }
    },

    // Execute immediate search (on Enter key)
    executeSearch() {
      if (this.search_service && this.search_query) {
        this.search_service.executeSearch(this.search_query);
      }
    },

    // Clear search
    clearSearch() {
      this.search_query = '';
      if (this.search_service) {
        this.search_service.clearSearch();
      }

      // Clear input field
      if (this.$refs.searchInput) {
        this.$refs.searchInput.value = '';
        this.$refs.searchInput.focus();
      }
    },

    // Handle focus/blur for UI states
    handleSearchFocus() {
      if (this.$store.galleryUI) {
        this.$store.galleryUI.search_focused = true;
      }
    },

    handleSearchBlur() {
      if (this.$store.galleryUI) {
        this.$store.galleryUI.search_focused = false;
      }
    },

    /**
     * Watch for product changes and refresh mobile carousel
     * @private
     */
    setupMobileCarouselWatcher() {
      let lastProductIds = '';
      let isFirstWatch = true;

      this.$watch(() => {
        const products = this.$store.galleryProducts.getCurrentProducts();
        // Hash product IDs to detect changes
        return products.map(p => p?.id).join(',');
      }, (newProductIds, oldProductIds) => {
        if (!this.isMobile || !this.isMobile()) return;

        // Skip initial watch trigger to prevent unnecessary refresh on mount
        if (isFirstWatch) {
          isFirstWatch = false;
          lastProductIds = newProductIds;
          return;
        }

        if (newProductIds === oldProductIds || newProductIds === lastProductIds) {
          return;
        }

        lastProductIds = newProductIds;

        // Skip if orientation update is in progress - it will handle the carousel update
        if (window._mobileCarouselOrientationUpdating || window._orientationChangeStartTime) {
          return;
        }

        // Trigger carousel refresh with small delay for DOM stability
        if (this.refreshMobileCarousel) {
          setTimeout(() => this.refreshMobileCarousel(), 100);
        }
      });
    }
  };
}
